import { Form, Formik } from 'formik';
import { useContext, useState } from 'react';
import useSWR from 'swr';
import { organization_levels_by_role } from '../../../../../../../../domain/roles';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import LINKS from '../../../../../configs/links';
import { useCopyToClipboard } from '../../../../../utils/copy';
import { showChat } from '../../../../../utils/liveChat';
import { isFreePlan } from '../../../../../utils/subscription';
import Alert from '../../../../common/base/Alert';
import Badge from '../../../../common/base/Badge';
import Button from '../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../common/base/Card';
import Container from '../../../../common/base/Container';
import Divider from '../../../../common/base/Divider';
import Icon from '../../../../common/base/Icon';
import Link from '../../../../common/base/Link';
import Skeleton from '../../../../common/base/Skeleton';
import Text from '../../../../common/base/Text';
import Tooltip from '../../../../common/base/Tooltip';
import CopyableField from '../../../../common/CopyableField';
import CustomDomainStatus from '../../../../common/CustomDomainStatus';
import { useDialog } from '../../../../common/Dialog';
import SelectInput from '../../../../common/Form/Fields/SelectInput';
import SwitchInput, { StyledSwitch } from '../../../../common/Form/Fields/SwitchInput';
import TextInput from '../../../../common/Form/Fields/TextInput';
import { Div } from '../../../../common/helpers/StyledUtils';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { DashboardContext } from '../../DashboardContext';
import { eg_static_ip_addresses } from './EnableStaticIPDialog';
import TeamNotifications from './TeamNotifications';
const TeamSettings: React.FC = () => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    organization,
    team,
    teams,
    mutateTeam,
    mutateTeams,
    resetContext,
    subscription,
    team_role
  } = useContext(DashboardContext);
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  const [deleting, setDeleting] = useState(false);
  const [is_toggling_static_ip, setIsTogglingStaticIP] = useState(false);
  const copyToClipboard = useCopyToClipboard();
  const static_ip_enabled_count = teams?.filter(t => t.static_ip_proxy).length || 0;

  // Determine Static IP access levels
  const is_free_plan = !!subscription?.plan && isFreePlan(subscription.plan);
  const is_custom_enterprise = !!organization?.feature_flags?.disable_throughput_change;
  const is_admin = team_role === 'admin';
  const can_self_serve_static_ip = !is_free_plan && !is_custom_enterprise && is_admin;
  const is_addon_enabled_org = static_ip_enabled_count > 0;
  const {
    data: custom_domains,
    mutate,
    isValidating,
    isLoading: custom_domains_loading
  } = useSWR(team && APIMethodKeys.teams.listCustomDomains(team.id), () => HookdeckAPI.teams.listCustomDomains(), {
    refreshInterval(latest) {
      if (latest?.some(domain => domain?.ssl.status === 'initializing')) {
        return 5 * 1000;
      }
      return 0;
    }
  });
  const handleSubmit = values => {
    return HookdeckAPI.teams.update(values).then(team_0 => {
      addToast('success', 'Project updated');
      mutateTeam(team_0);
      return true;
    }).catch(() => {
      addToast('error', 'Failed to update project');
    });
  };
  const handleStaticIPToggle = async (enabled: boolean) => {
    if (enabled) {
      setIsTogglingStaticIP(true);
      try {
        await HookdeckAPI.billing.enableAddonBulk('static_ip', [team!.id]);
        mutateTeam({
          ...team!,
          static_ip_proxy: true
        });
        mutateTeams?.();
        addToast('success', `Static IP delivery enabled for ${team!.name}.`);
      } catch (error: any) {
        const error_message = error?.response?.message || 'Failed to enable Static IP. Please try again.';
        addToast('error', error_message);
      } finally {
        setIsTogglingStaticIP(false);
      }
    } else {
      showDialog(async () => {
        setIsTogglingStaticIP(true);
        try {
          await HookdeckAPI.billing.disableAddonBulk('static_ip', [team!.id]);
          mutateTeam({
            ...team!,
            static_ip_proxy: false
          });
          await (mutateTeams as () => Promise<unknown>)?.();
          addToast('success', `Static IP delivery disabled for ${team!.name}.`);
        } catch (error_0: any) {
          const error_message_0 = error_0?.response?.message || 'Failed to disable Static IP. Please try again.';
          addToast('error', error_message_0);
          throw error_0;
        } finally {
          setIsTogglingStaticIP(false);
        }
      }, undefined, {
        title: 'Disable Static IP delivery',
        submit_label: static_ip_enabled_count === 1 ? 'Disable Static IP delivery' : 'Disable for 1 project',
        cancel_label: 'Cancel',
        danger: true,
        message: <Div>
              {static_ip_enabled_count === 1 && <Alert danger inline m={{
            b: 4
          }}>
                  <Text bold>This action will disable Static IP delivery on your organization</Text>
                  <Text>
                    This is the sole project supporting Static IP delivery on this organization.
                    Disabling will also turn off the feature at the organization level.
                  </Text>
                </Alert>}
              <Text as="p" m={0}>
                Are you sure you&apos;d like to disable Static IP delivery on{' '}
                <Text as="span" semi>
                  {team!.name}
                </Text>
                ? If destinations on this project have IP allowlisting configured, webhook
                deliveries may begin to fail.
              </Text>
            </Div>
      });
    }
  };
  const showDomainInput = () => showDialog((values_0: {
    hostname: string;
  }) => {
    return HookdeckAPI.teams.createCustomDomain(values_0.hostname).then(() => {
      addToast('success', 'The domain added, complete the DNS configurations');
      mutate();
    }).catch(e => {
      // TODO: Show those errors inside the form
      if (e.response?.code === 'CUSTOM_DOMAIN_INVALID') {
        addToast('error', 'The domain entered is not a valid hostname.');
      }
      if (e.response?.code === 'CUSTOM_DOMAIN_ALREADY_EXISTS') {
        addToast('error', 'The domain entered is already in use.');
      }
      throw e;
    });
  }, undefined, {
    title: 'Set a custom domain',
    submit_label: 'Configure',
    cancel_label: 'Cancel',
    form_props: {
      initial_values: {
        hostname: ''
      },
      validate: ({
        hostname
      }: {
        hostname: string;
      }) => {
        if (!hostname || hostname.length < 0) return {
          hostname: 'Required'
        };
        if (!/^([a-z0-9]([a-z0-9\-]{0,61}[a-z0-9])?\.)+[a-z0-9\-]{2,61}\.[a-z]{2,}$/.test(hostname)) {
          return {
            hostname: 'Invalid hostname. Hostname must contain a subdomain.'
          };
        }
        return {};
      },
      Fields: () => <TextInput m={0} label="Domain" name="hostname" placeholder="events.example.com" help="Valid domain where a TXT & CNAME DNS record will need to be configured." required />
    }
  });
  const onDomainRefresh = () => {
    mutate();
  };
  const onDomainDeleted = (domain_id: string) => {
    showDialog(() => {
      setDeleting(true);
      return HookdeckAPI.teams.deleteCustomDomain(domain_id).then(() => {
        addToast('success', 'Domain removed');
        mutate((domains = []) => domains.filter(d => d.id !== domain_id));
      }).catch(() => {
        addToast('error', 'The domain could not be removed, try again later or contact us.');
      }).finally(() => {
        setDeleting(false);
      });
    }, undefined, {
      submit_label: 'Remove domain',
      cancel_label: 'Cancel',
      submit_icon: 'delete',
      danger: true,
      message: `Are you sure you want to remove the domain? All URLs using this domain will stop working.`
    });
  };
  const onTransfer = () => showDialog((values_1: {
    organization_id: string;
  }) => {
    return HookdeckAPI.teams.transfer(values_1.organization_id).then(() => {
      resetContext();
    }).catch(() => {
      addToast('error', 'The project could not be tranfered, please contact us.');
    });
  }, undefined, {
    title: 'Transfer project',
    submit_label: 'Transfer project',
    cancel_label: 'Cancel',
    form_props: {
      initial_values: {
        organization_id: ''
      },
      validate: ({
        organization_id
      }: {
        organization_id: string;
      }) => {
        if (!organization_id) return {
          organization_id: 'Required'
        };
        return {};
      },
      Fields: () => {
        const {
          HookdeckAPI: HookdeckAPI_0
        } = useContext(GlobalContext);
        const {
          data: organizations
        } = useSWR(APIMethodKeys.organizations.listAll(), () => HookdeckAPI_0.organizations.listAll());
        return <>
                <Text m={{
            b: 4
          }}>
                  Admins and project admins are able to transfer projects to organizations where
                  they are an admin. Projects maintain their access status (Open or Private) in the
                  organization they are transferred to.
                </Text>
                <Text bold m={{
            b: 4
          }}>
                  Transfer considerations:
                </Text>
                <Div flex={{
            align: 'flex-start'
          }} m={{
            b: 4
          }}>
                  <Icon icon="lock" left m={{
              t: 0.5
            }} />
                  <Div>
                    <Text semi>Project access</Text>
                    <Text muted>
                      Project access is not maintained when transferring a project to an
                      organization. If the project is private all members will lose access and will
                      need to be re-added
                    </Text>
                  </Div>
                </Div>
                <Div flex={{
            align: 'flex-start'
          }} m={{
            b: 4
          }}>
                  <Icon icon="construction" left m={{
              t: 0.5
            }} />
                  <Div>
                    <Text semi>Features</Text>
                    <Text muted>
                      Projects inherit features from their parent organizations. If you move this
                      project to an organization with a lesser plan, you may lose feature access.
                    </Text>
                  </Div>
                </Div>
                <Div flex={{
            align: 'flex-start'
          }} m={{
            b: 4
          }}>
                  <Icon icon="progress_activity" left m={{
              t: 0.5
            }} />
                  <Div>
                    <Text semi>No downtime</Text>
                    <Text muted>
                      You won’t experience any downtime when transferring a project.
                    </Text>
                  </Div>
                </Div>
                <Div flex={{
            align: 'flex-start'
          }} m={{
            b: 4
          }}>
                  <Icon icon="rate_limit" left m={{
              t: 0.5
            }} />
                  <Div>
                    <Text semi>Throughput transfer</Text>
                    <Text muted>
                      If the target organization is on a paid plan, the project’s throughput and fee
                      will be transfered to the new organization. If the organization is on a free
                      plan, the project throughput will be reset to the free plan limits.
                    </Text>
                  </Div>
                </Div>
                <Divider m={{
            y: 6
          }} />
                <SelectInput block label="Target organization" help="The organization you’d like your project transferred to." placeholder="Select target organization" name="organization_id" flex={{
            grow: true,
            direction: 'column'
          }} search dropdown={{
            parent_width: true,
            placement: 'bottom-end'
          }} options={organizations?.filter(organization_0 => organization_levels_by_role[organization_0.role] >= organization_levels_by_role.admin && organization_0.id !== team!.organization_id).map(organization_1 => ({
            label: organization_1.name,
            value: organization_1.id
          })) || []} />
              </>;
      }
    }
  });
  const onDelete = () => showDialog(() => {
    return HookdeckAPI.teams.delete().then(() => {
      resetContext();
    }).catch(() => {
      addToast('error', 'The project could not be deleted, please contact us.');
    });
  }, undefined, {
    title: 'Delete project',
    submit_label: 'Delete project',
    cancel_label: 'Cancel',
    submit_icon: 'delete',
    danger: true,
    form_props: {
      initial_values: {
        name: ''
      },
      validate: ({
        name
      }: {
        name: string;
      }) => {
        if (!name || name.length < 0) return {
          name: 'Required'
        };
        if (name !== team!.name) {
          return {
            name: 'Project name does not match.'
          };
        }
        return {};
      },
      Fields: () => <>
              <Text m={{
          b: 4
        }}>
                This action can’t be undone. You’ll lose all data associated with this project,
                including connection configuration, routing logic, and event history. Are you sure?{' '}
                <br />
                <br />
                If you’d like to delete this project, type <strong>{team!.name}</strong> below to
                confirm.
              </Text>
              <TextInput m={0} name="name" placeholder={team!.name} required focus />
            </>
    }
  });
  return <>
      <Container medium left m={{
      b: 10
    }}>
        <Formik initialValues={{
        name: team!.name
      }} validate={values_2 => {
        const errors: Partial<typeof values_2> = {};
        if (!values_2.name || values_2.name.length === 0) {
          errors.name = 'Required';
        }
        if (values_2.name && values_2.name.length > 40) {
          errors.name = 'Name is longer then 40 characters';
        }
        return errors;
      }} onSubmit={handleSubmit}>
          {props => <Form>
              <Text bold size="l" as="h2" m={{
            t: 0,
            b: 4
          }}>
                Project name
              </Text>
              <TextInput name="name" required m={{
            b: 5
          }} />
              <Button.Permission primary disabled={!props.isValid || !props.touched || props.isSubmitting || team!.name === props.values.name} submit icon={props.isSubmitting ? 'loading' : 'save'}>
                Save
              </Button.Permission>
            </Form>}
        </Formik>
      </Container>
      <Container medium left m={{
      b: 10
    }}>
        <Text bold size="l" as="h2" m={{
        t: 0,
        b: 4
      }}>
          Project ID
        </Text>
        <CopyableField mono value={team!.id} />
      </Container>
      <Container medium left m={{
      b: 10
    }}>
        <Text bold size="l" as="h2" m={{
        b: 0
      }}>
          Custom domain
        </Text>
        <Text muted m={{
        b: 4
      }}>
          Receive requests on a custom URL.
        </Text>
        {custom_domains_loading && <Skeleton w={100} h={{
        px: 38
      }} />}
        {custom_domains && custom_domains.length === 0 && <Div flex={{
        align: 'center'
      }}>
            <Button.Permission icon="add" outline onClick={showDomainInput}>
              Add custom domain
            </Button.Permission>
          </Div>}
        {custom_domains && custom_domains.length > 0 && custom_domains.map(domain_0 => <CustomDomainStatus key={domain_0.id} custom_domain={domain_0} cname_target="hkdk.events" is_validating={isValidating} is_deleting={deleting} onRefresh={onDomainRefresh} onDelete={() => onDomainDeleted(domain_0.id)} />)}
      </Container>
      <Div m={{
      x: 6,
      b: 8
    }}>
        <Divider />
      </Div>
      <Container medium left m={{
      b: 10
    }}>
        <Text bold size="l" as="h2" m={{
        b: 4
      }}>
          Delivery
        </Text>
        <StyledCard m={{
        b: 4
      }}>
          <StyledCardSection flex={{
          justify: 'space-between',
          align: 'center'
        }} p={{
          x: 4,
          y: 3
        }}>
            <Div flex={{
            align: 'center',
            gap: 3
          }}>
              {can_self_serve_static_ip ? <StyledSwitch as="label" has_label={false} disabled={is_toggling_static_ip || can_self_serve_static_ip && !is_addon_enabled_org} onClick={e_0 => {
              e_0.preventDefault();
              handleStaticIPToggle(!team!.static_ip_proxy);
            }}>
                  <input checked={team!.static_ip_proxy} disabled={is_toggling_static_ip || can_self_serve_static_ip && !is_addon_enabled_org} type="checkbox" readOnly />
                </StyledSwitch> : <Tooltip tooltip={is_free_plan ? 'Static IP is a paid feature. Upgrade to a paid plan to enable.' : !is_admin ? 'Only project admins can toggle Static IP delivery.' : undefined} disabled={is_custom_enterprise}>
                  <StyledSwitch as="label" has_label={false} disabled>
                    <input checked={team!.static_ip_proxy} disabled type="checkbox" />
                  </StyledSwitch>
                </Tooltip>}
              <Div flex={{
              align: 'center',
              gap: 1
            }}>
                <Text shimmering={is_toggling_static_ip}>Delivery from Static IPs</Text>
                <Tooltip tooltip="Static IPs are used to deliver events through a specific set of IPs that can be whitelisted.">
                  <Icon muted icon="info" />
                </Tooltip>
              </Div>
            </Div>
            {is_custom_enterprise && <Link as="button" onClick={showChat}>
                Contact us to {team!.static_ip_proxy ? 'disable' : 'enable'}
              </Link>}
            {is_free_plan && !is_custom_enterprise && <Link to="/settings/organization/plans" icon="link">
                Upgrade plan
              </Link>}
            {can_self_serve_static_ip && !is_addon_enabled_org && <Link to="/settings/organization/billing/addons/static_ip/enable" icon="link">
                Enable via Billing
              </Link>}
          </StyledCardSection>
          {team!.static_ip_proxy && <StyledCardSection p={4} flex={{
          justify: 'space-between',
          align: ''
        }}>
              <StyledCard overflow_hidden>
                <StyledCardSection muted p={3} flex={{
              direction: 'column',
              align: 'flex-start',
              gap: 3
            }}>
                  <Div w={100} flex={{
                align: 'center',
                justify: 'space-between',
                gap: 2
              }}>
                    <Text semi size="s">
                      Dedicated Hookdeck IP addresses
                    </Text>
                    <Button ghost icon="copy" m={-1} onClick={() => copyToClipboard(eg_static_ip_addresses.join(', '))} />
                  </Div>
                  <Div flex={{
                align: 'flex-start',
                gap: 2
              }}>
                    {eg_static_ip_addresses.map(address => <Badge mono subtle neutral key={address}>
                        {address}
                      </Badge>)}
                  </Div>
                </StyledCardSection>
              </StyledCard>
            </StyledCardSection>}
        </StyledCard>

        <Formik initialValues={{
        headers_prefix: team!.headers_prefix || '',
        enable_headers_prefix: !!team!.headers_prefix
      }} validate={values_3 => {
        const errors_0: Partial<typeof values_3> = {};
        if (values_3.enable_headers_prefix && !values_3.headers_prefix) {
          errors_0.headers_prefix = 'Required';
        }
        return errors_0;
      }} onSubmit={values_4 => handleSubmit({
        headers_prefix: values_4.enable_headers_prefix ? values_4.headers_prefix || null : null
      })}>
          {props_0 => <Form>
              <StyledCard m={{
            b: 5
          }}>
                <StyledCardSection flex={{
              justify: 'flex-start',
              align: 'center',
              gap: 3
            }} p={{
              x: 4,
              y: 3
            }}>
                  <SwitchInput name="enable_headers_prefix" />
                  <Div flex={{
                align: 'center',
                gap: 1
              }}>
                    <Text>Use custom HTTP headers prefix</Text>
                    <Tooltip tooltip="White-label the default 'X-Hookdeck' headers prefix.">
                      <Icon muted icon="info" />
                    </Tooltip>
                  </Div>
                </StyledCardSection>
                {props_0.values.enable_headers_prefix && <>
                    <StyledCardSection p={4}>
                      <TextInput m={0} label="Headers Prefix" name="headers_prefix" placeholder="x-hookdeck-eventid" help={`Headers must start with ${props_0.values.headers_prefix.toLowerCase() || 'x-hookdeck'}, e.g. ${props_0.values.headers_prefix.toLowerCase() || 'x-hookdeck'}-eventid`} />
                    </StyledCardSection>
                    <StyledCardSection p={{
                x: 4,
                y: 3
              }}>
                      <Link neutral href={LINKS.product_docs.projects} icon="link">
                        <Text size="xs" semi as="span">
                          White-labeling disables some headers that shouldn't be public-facing
                        </Text>
                      </Link>
                    </StyledCardSection>
                  </>}
              </StyledCard>
              <Button.Permission primary disabled={!props_0.isValid || !props_0.dirty || props_0.isSubmitting} submit icon={props_0.isSubmitting ? 'loading' : 'save'}>
                Save
              </Button.Permission>
            </Form>}
        </Formik>
      </Container>
      <Div m={{
      x: 6,
      b: 8
    }}>
        <Divider />
      </Div>
      <Container medium left m={{
      b: 10
    }}>
        <TeamNotifications />
      </Container>
      <Div m={{
      x: 6,
      b: 8
    }}>
        <Divider />
      </Div>
      <Container medium left m={{
      b: 10
    }}>
        <Text bold size="l" as="h2" m={{
        b: 0
      }}>
          Transfer project
        </Text>
        <Text m={{
        b: 4
      }} muted>
          Move this project and all associated data to a different organization.
        </Text>
        <Button.Permission role="admin" icon="transfer" outline onClick={onTransfer}>
          Transfer project
        </Button.Permission>
      </Container>
      <Container medium left m={{
      b: 10
    }}>
        <Text bold size="l" as="h2" m={0}>
          Delete project
        </Text>
        <Text m={{
        b: 4
      }} muted>
          Permanently delete this project and all associated data.
        </Text>
        <Button.Permission role="admin" outline danger icon="delete" onClick={onDelete}>
          Delete project
        </Button.Permission>
      </Container>
    </>;
};
export default TeamSettings;